import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { ModalContainerComponent } from '../../../shared/components/modal-container/modal-container.component';
import { MatGridListModule } from '@angular/material/grid-list';
import { MatFormFieldModule } from '@angular/material/form-field';
import { TranslateModule } from '@ngx-translate/core';
import { IBreadcrumbs } from '../../../shared/components/breadcrumbs/breadcrumbs.component';
import { MatSelectModule } from '@angular/material/select';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { FacilitiesService } from '../../../core/services/facilities.service';
import { catchError, map, mergeAll, mergeMap, of, take, tap, toArray } from 'rxjs';
import { IFormChangeEmitter } from '../../../shared/components/form/form.component';
import { RequestsService } from '../../../core/services/utils/requests.service';
import { SensorService } from '../../../core/services/sensors.service';
import { ToastService } from '../../../core/services/utils/toast.service';
import { IonicColors } from '../../../shared/enums/ionicColors.enum';
import { IonButton, IonContent, IonSpinner } from "@ionic/angular/standalone";
import { EventTypeCodes } from '../../../shared/models/be/notification';
import { IDevice } from '../../../shared/models/be/device';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { LoggerService } from '../../../core/services/utils/logger.service';
import { AnalyticsService } from '../../../core/services/analytics.service';
import { IAnalyticsAzureCostByService } from '../../../shared/models/be/analytics';
import { IAnalyticsAzureVMMetrics } from '../../../shared/models/analytics';
import { NgxEchartsDirective, provideEchartsCore } from 'ngx-echarts';
import * as echarts from 'echarts/core';
import { BarChart, PieChart } from 'echarts/charts';
import { GridComponent, LegendComponent, TooltipComponent } from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';
import { EChartsOption } from 'echarts/types/dist/shared';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';

echarts.use([PieChart, BarChart, TooltipComponent, LegendComponent, GridComponent, CanvasRenderer]);

@Component({
  selector: 'app-settings',
  templateUrl: './developerSettings.page.html',
  styleUrls: ['./developerSettings.page.scss'],
  imports: [IonContent,
    CommonModule,
    ModalContainerComponent,
    MatGridListModule,
    MatFormFieldModule,
    TranslateModule,
    MatSelectModule,
    ReactiveFormsModule,
    FormsModule,
    IonContent,
    IonButton,
    IonSpinner,
    MatCheckboxModule,
    MatIconModule,
    MatTooltipModule,
    NgxEchartsDirective
  ],
  providers: [provideEchartsCore({ echarts })]
})
export class DeveloperSettingsPage implements OnInit {

  public currentLanguage: string = 'it';

  public formStatus: IFormChangeEmitter = {
    canSaveDraft: true,
    canSaveData: true,
  }

  public breadcrumbsList: IBreadcrumbs[] = [{
    icon: 'settings',
    routerLink: '',
    label: 'DEVELOPER_SETTINGS'
  }]

  public azureCostsLoading = false;
  public azureVmMetricsLoading = false;
  public azureCostsData: IAnalyticsAzureCostByService[] = [];
  public azureVmMetricsData: IAnalyticsAzureVMMetrics[] = [];
  public azureCostsOptions: EChartsOption = {};
  public azureVmMetricsOptions: EChartsOption = {};
  private readonly azureCostPalette = [
    '#3b82f6', '#22c6c8', '#76bf43', '#f5a623', '#8b5cf6',
    '#14b8a6', '#ff5b5d', '#7c91b9', '#60a5fa', '#c084fc'
  ];

  public settingsData: ISettingsData[] = [
    {
      id: 'notifications',
      label: 'NOTIFICATION_SIMULATION',
      rowSpan: 1,
      inputs: [
        {
          id: 'device',
          type: 'select',
          label: 'DEVICE',
          length: 2,
          disableTranslation: true,
          onSelect: () => { },
          values: []
        }, {
          id: 'eventType',
          type: 'select',
          length: 2,
          label: 'EVENT_TYPE',
          disableTranslation: true,
          onSelect: (resourceId) => {
            const notificationSection = this.settingsData.find((setting) => setting.id === 'notifications')!;
            const deviceInput = notificationSection.inputs.find((input) => input.id === 'device')!;
            const deviceSelectedValue = deviceInput.values?.find((value) => value.id === deviceInput.selectedValue);
            const bedInput = notificationSection.inputs.find((input) => input.id === 'bed')!;
            const sendButton = notificationSection.inputs.find((input) => input.id === 'sendNotification')!;
            if (['04', '05'].includes(resourceId as string) && notificationSection?.inputs.find((input) => input.id === 'device' && input.selectedValue)) {
              bedInput && (bedInput.hideSection = false);
              notificationSection.rowSpan = 2;
              deviceSelectedValue && this.facilitiesService.getBeds$((deviceSelectedValue as any).roomId).pipe(
                take(1),
                tap((beds) => {
                  bedInput.values = beds.map((bed) => ({
                    id: bed.id!,
                    label: bed.name!
                  }));
                })
              ).subscribe();
            } else {
              bedInput.hideSection = true;
              bedInput.selectedValue = null;
              notificationSection.rowSpan = 1;
              sendButton.disabled = !deviceSelectedValue;
            }
          },
          values: [{
            id: '01',
            label: 'Caduta'
          }, {
            id: '02',
            label: 'Entrata stanza'
          }, {
            id: '03',
            label: 'Uscita stanza'
          }, {
            id: '04',
            label: 'Entrata letto'
          }, {
            id: '05',
            label: 'Uscita letto'
          }, {
            id: '06',
            label: 'Device offline'
          }, {
            id: '07',
            label: 'Device online'
          }, {
            id: '08',
            label: 'Test'
          }, {
            id: '09',
            label: 'Errore sensore'
          }, {
            id: '10',
            label: 'Ingresso bagno'
          }
          ]
        }, {
          id: 'bed',
          type: 'select',
          label: 'BED',
          length: 2,
          hideSection: true,
          disableTranslation: true,
          values: [],
          onSelect: () => {
            const notificationSection = this.settingsData.find((setting) => setting.id === 'notifications')!;
            const sendButton = notificationSection.inputs.find((input) => input.id === 'sendNotification')!;
            sendButton.disabled = false;
          }
        }, {
          id: 'sendNotification',
          type: 'button',
          length: 2,
          label: 'SEND_NOTIFICATION',
          disabled: true,
          disableTranslation: true,
          onSelect: () => this.sendNotification()
        }
      ]
    }, {
      id: 'token',
      label: 'FCM_TOKEN_INFO',
      rowSpan: 4,
      inputs: [
        {
          id: 'fcmToken',
          type: 'text',
          label: 'Token FCM',
          length: 6,
          rowspan: 2,
          disableTranslation: true,
          value: localStorage.getItem('FCM_TOKEN') || "-"
        }, {
          id: 'tokenRegistration',
          type: 'text',
          length: 6,
          label: 'Registrazione token/building (sessione)',
          disableTranslation: true,
          value: "-"
        }, {
          id: 'currentBuilding',
          type: 'text',
          length: 6,
          label: 'Building corrente',
          disableTranslation: true,
          value: "-"
        }
      ]
    }
  ];


  constructor(
    private facilitiesService: FacilitiesService,
    private requestsService: RequestsService,
    private sensorService: SensorService,
    private toastService: ToastService,
    private loggerService: LoggerService,
    private analyticsService: AnalyticsService
  ) { }

  ngOnInit() {
    this.loadDeveloperSettings();
    if (this.isSuperUser) {
      this.loadAzureInfrastructureData();
    }
  }

  get isSuperUser(): boolean {
    return this.requestsService.getCurrentUser()?.role === 'superuser';
  }

  get azureCostItems(): Array<{ name: string; value: number; percentage: number; color: string }> {
    const values = this.azureCostsData
      .map((item, index) => ({
        name: String(item?.name ?? 'Altro'),
        value: Math.max(0, Number(item?.value ?? 0)),
        color: this.azureCostPalette[index % this.azureCostPalette.length]
      }))
      .filter(item => item.value > 0)
      .sort((a, b) => b.value - a.value);
    const total = values.reduce((sum, item) => sum + item.value, 0);

    return values.map(item => ({
      ...item,
      percentage: total ? (item.value / total) * 100 : 0
    }));
  }

  get azureCostTotal(): number {
    return this.azureCostItems.reduce((sum, item) => sum + item.value, 0);
  }

  private loadAzureInfrastructureData(): void {
    this.loadAzureCosts();
    this.loadAzureVmMetrics();
  }

  private loadAzureCosts(): void {
    this.azureCostsLoading = true;
    this.analyticsService.getKpiCost$('7d').pipe(take(1)).subscribe({
      next: data => {
        this.azureCostsData = data ?? [];
        this.azureCostsOptions = this.buildAzureCostsOptions();
        this.azureCostsLoading = false;
      },
      error: err => {
        this.loggerService.error('Errore caricamento costi Azure', err);
        this.azureCostsData = [];
        this.azureCostsOptions = this.buildAzureCostsOptions();
        this.azureCostsLoading = false;
      }
    });
  }

  private loadAzureVmMetrics(): void {
    this.azureVmMetricsLoading = true;
    this.analyticsService.getAzureVMMetrics$().pipe(take(1)).subscribe({
      next: data => {
        this.azureVmMetricsData = data ?? [];
        this.azureVmMetricsOptions = this.buildAzureVmMetricsOptions();
        this.azureVmMetricsLoading = false;
      },
      error: err => {
        this.loggerService.error('Errore caricamento metriche Virtual Machines Azure', err);
        this.azureVmMetricsData = [];
        this.azureVmMetricsOptions = this.buildAzureVmMetricsOptions();
        this.azureVmMetricsLoading = false;
      }
    });
  }

  private buildAzureCostsOptions(): EChartsOption {
    return {
      animationDuration: 450,
      tooltip: {
        trigger: 'item',
        confine: true,
        backgroundColor: 'rgba(20, 31, 52, 0.94)',
        borderWidth: 0,
        padding: [8, 10],
        textStyle: { color: '#fff', fontSize: 12 },
        formatter: (params: any) => {
          const value = Number(params?.value ?? 0).toLocaleString('it-IT', { maximumFractionDigits: 2 });
          const percentage = Number(params?.percent ?? 0).toLocaleString('it-IT', { maximumFractionDigits: 1 });
          return params.marker + params.name + '<br/><strong>' + value + ' EUR</strong> (' + percentage + '%)';
        }
      },
      series: [{
        type: 'pie',
        radius: ['57%', '79%'],
        center: ['50%', '50%'],
        minAngle: 3,
        label: { show: false },
        selectedMode: false,
        emphasis: { scale: false, focus: 'none' },
        itemStyle: { borderRadius: 5, borderColor: '#fff', borderWidth: 2 },
        data: this.azureCostItems.map(item => ({
          name: item.name,
          value: item.value,
          itemStyle: { color: item.color, borderRadius: 5, borderColor: '#fff', borderWidth: 2 }
        }))
      }]
    };
  }

  private buildAzureVmMetricsOptions(): EChartsOption {
    const vmNames = this.azureVmMetricsData.map(vm => String(vm.vmName ?? 'Virtual machine'));
    const metricValue = (vm: IAnalyticsAzureVMMetrics, metricName: string): number => {
      const metric = (vm.metrics ?? []).find(item => item.metricName === metricName);
      return Math.max(0, Number(metric?.averageValue ?? 0));
    };
    const cpuData = this.azureVmMetricsData.map(vm => metricValue(vm, 'Percentage CPU'));
    const ramData = this.azureVmMetricsData.map(vm => metricValue(vm, 'Available Memory'));

    return {
      animationDuration: 450,
      tooltip: {
        trigger: 'axis',
        confine: true,
        axisPointer: { type: 'shadow', shadowStyle: { color: 'rgba(37, 116, 255, 0.06)' } },
        backgroundColor: 'rgba(20, 31, 52, 0.94)',
        borderWidth: 0,
        padding: [9, 11],
        textStyle: { color: '#fff', fontSize: 12 },
        formatter: (params: any) => {
          const entries = Array.isArray(params) ? params : [params];
          const vmName = String(entries[0]?.axisValue ?? 'Virtual machine');
          const rows = entries.map((entry: any) => {
            const value = Number(entry?.value ?? 0).toLocaleString('it-IT', { maximumFractionDigits: 2 });
            const unit = entry?.seriesIndex === 0 ? '%' : ' GB';
            return entry.marker + entry.seriesName + ': <strong>' + value + unit + '</strong>';
          });
          return '<strong>' + vmName + '</strong><br/>' + rows.join('<br/>');
        }
      },
      legend: {
        top: 2,
        left: 0,
        icon: 'roundRect',
        itemWidth: 12,
        itemHeight: 8,
        itemGap: 22,
        textStyle: { color: '#526078', fontSize: 12 }
      },
      grid: { top: 52, left: 58, right: 68, bottom: 76 },
      xAxis: {
        type: 'category',
        data: vmNames,
        axisTick: { show: false },
        axisLine: { lineStyle: { color: '#dfe5ee' } },
        axisLabel: {
          interval: 0,
          rotate: vmNames.length > 4 ? 24 : 0,
          margin: 15,
          color: '#647188',
          fontSize: 11,
          formatter: (value: string) => value.length > 22 ? value.substring(0, 20) + '...' : value
        }
      },
      yAxis: [
        {
          type: 'value',
          name: 'CPU',
          position: 'left',
          min: 0,
          axisLabel: {
            color: '#6f7b90',
            fontSize: 11,
            formatter: (value: number) => value.toLocaleString('it-IT', { maximumFractionDigits: 1 }) + ' %'
          },
          nameTextStyle: { color: '#526078', fontSize: 12, padding: [0, 0, 4, 0] },
          axisLine: { show: false },
          splitLine: { lineStyle: { color: '#e9edf3', type: 'dashed' } }
        },
        {
          type: 'value',
          name: 'RAM libera',
          position: 'right',
          min: 0,
          axisLabel: {
            color: '#6f7b90',
            fontSize: 11,
            formatter: (value: number) => value.toLocaleString('it-IT', { maximumFractionDigits: 1 }) + ' GB'
          },
          nameTextStyle: { color: '#526078', fontSize: 12, padding: [0, 0, 4, 0] },
          axisLine: { show: false },
          splitLine: { show: false }
        }
      ],
      series: [
        {
          name: 'CPU',
          type: 'bar',
          yAxisIndex: 0,
          data: cpuData,
          barMaxWidth: 30,
          itemStyle: { color: '#3b82f6', borderRadius: [5, 5, 0, 0] }
        },
        {
          name: 'RAM libera',
          type: 'bar',
          yAxisIndex: 1,
          data: ramData,
          barMaxWidth: 30,
          itemStyle: { color: '#76bf43', borderRadius: [5, 5, 0, 0] }
        }
      ]
    };
  }

  loadDeveloperSettings() {
    this.sensorService.getDevices$({ buildingId: this.requestsService.getCurrentUser()?.building?.id }).pipe(
      map((devicesData) => devicesData.devices),
      mergeAll(),
      mergeMap((device) => this.sensorService.isSensorSilenced$(device.facilities!.room!.id).pipe(
        take(1),
        map((silencedStatus) => ({
          ...device,
          silencedStatus
        }))
      )),
      toArray(),
      catchError(() => of([])),
      take(1),
      tap((devices) => {
        this.settingsData.find((setting) => setting.id === 'notifications')!.inputs[0].values = (devices as (IDevice & { silencedStatus: { fall: boolean; room: boolean; bed: boolean } })[]).map((device) => ({
          id: device.label,
          label: `${device.label} (${device.facilities?.room?.name}) ${(device.silencedStatus.bed && device.silencedStatus.fall && device.silencedStatus.room) ? '- TEMPORIZZATO' : (device.silencedStatus.bed || device.silencedStatus.fall || device.silencedStatus.room) ? '- PARZ. TEMPORIZZATO' : ''}`,
          disabled: device.silencedStatus.bed && device.silencedStatus.fall && device.silencedStatus.room,
          roomId: device.facilities?.room?.id
        }));
        this.refreshTokenDebugInfo();
      })
    ).subscribe();
  }

  sendNotification() {
    const developerSettings = this.settingsData.find((setting) => setting.id === 'notifications');
    const buildingId = this.requestsService.getCurrentUser()?.building?.id;
    const eventType = developerSettings?.inputs.find((input) => input.id === 'eventType')?.selectedValue as EventTypeCodes;
    const deviceId = developerSettings?.inputs.find((input) => input.id === 'device')?.selectedValue as string;
    const bedId = developerSettings?.inputs.find((input) => input.id === 'bed')?.selectedValue as string;
    if (buildingId && eventType && deviceId) {
      this.sensorService.sendEvent$(deviceId, eventType, buildingId || undefined, bedId || undefined).pipe(
        tap(() => this.loggerService.info("Notifica inviata correttamente")),
        catchError((err) => {
          this.toastService.showToast("Errore nell'invio della notifica", IonicColors.RED)
          throw err;
        })
      ).subscribe();
    } else {
      this.toastService.showToast("Errore nella compilazione della form, ricontrollare!", IonicColors.RED)
    }
  }

  get tableRowSpan(): number {
    return this.settingsData.reduce((prev, curr) => prev + curr.rowSpan, 0) + 2
  }

  private refreshTokenDebugInfo() {
    const currentBuildingId = this.requestsService.getCurrentUser()?.building?.id || '-';
    const registeredBuildingId = localStorage.getItem('FCM_REGISTERED_BUILDING_ID');
    const registeredToken = localStorage.getItem('FCM_REGISTERED_TOKEN');
    const currentToken = localStorage.getItem('FCM_TOKEN');
    const registrationStatus = registeredBuildingId && registeredToken && registeredBuildingId === currentBuildingId && registeredToken === currentToken
      ? 'Token associato al building corrente in questa sessione'
      : registeredBuildingId && registeredToken
        ? `Token associato localmente al building ${registeredBuildingId} in questa sessione`
        : 'Token non ancora registrato in questa sessione';

    this.settingsData.find((setting) => setting.id === 'token')!.inputs.find((input) => input.id === 'fcmToken')!.value = currentToken || '-';
    this.settingsData.find((setting) => setting.id === 'token')!.inputs.find((input) => input.id === 'tokenRegistration')!.value = registrationStatus;
    this.settingsData.find((setting) => setting.id === 'token')!.inputs.find((input) => input.id === 'currentBuilding')!.value = currentBuildingId;
  }
}

export interface ISettingsData {
  id: string,
  label: string,
  rowSpan: number,
  inputs: {
    id: string,
    type: 'select' | 'status' | 'button' | 'text' | 'checkBox',
    rowspan?: number;
    length: number,
    label: string,
    fill?: string,
    selectedValue?: string | boolean | null,
    disableTranslation?: boolean,
    hideSection?: boolean,
    disabled?: boolean,
    touched?: boolean,
    values?: {
      id: string,
      label: string,
      status?: boolean
    }[],
    value?: string;
    onSelect?: (resourceId: string | boolean) => void;
  }[]
}
